import { Quote, ShieldCheck, TrendingUp, MapPin } from "lucide-react";
import { BRAND } from "@/config/brand";
import { TESTIMONIALS, PAM_CASE_STUDY } from "@/config/testimonials";
import KitTicker from "@/pages/KitTicker";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";

export default function TestimonialsWall() {
  return (
    <section id="testimonials"
             className="relative z-10 border-t border-b"
             style={{ borderColor: C.line, background: `linear-gradient(180deg, ${C.ink} 0%, #0B1524 60%, ${C.ink} 100%)` }}
             data-testid="testimonials-section">
      <KitTicker />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <PamCaseStudy />

        {/* Header */}
        <div className="mt-24 md:mt-32 max-w-[820px]">
          <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: teal }}>
            What Kit&apos;s friends have said
          </div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02]"
              style={{ fontSize: "clamp(2rem, 4.4vw, 3.8rem)", color: "white" }}
              data-testid="testimonials-headline">
            The people who&apos;ve{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>actually worked with Kit</span>.
          </h2>
          <p className="mt-4 text-lg" style={{ color: C.textDim }}>
            No stock quotes. No fake reviews. Just what real friends and clients have said about
            working with Kit — the wall grows as more roll in.
          </p>
        </div>

        {/* Wall */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-[1000px]">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={i} t={t} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PamCaseStudy() {
  const p = PAM_CASE_STUDY;
  return (
    <div className="relative rounded-3xl p-8 md:p-14 border overflow-hidden"
         style={{
           background: `linear-gradient(135deg, ${teal}22 0%, ${gold}12 100%), ${C.inkSoft}`,
           borderColor: `${teal}55`,
           boxShadow: `0 80px 200px -60px ${teal}66`,
         }}
         data-testid="pam-case-study">
      <div aria-hidden className="absolute inset-0 opacity-25 pointer-events-none"
           style={{
             backgroundImage: `radial-gradient(600px 300px at 90% -10%, ${gold}55, transparent 60%),
                               radial-gradient(600px 300px at 10% 110%, ${teal}66, transparent 60%)`,
           }} />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-5"
             style={{ background: `${gold}22`, color: gold, border: `1px solid ${gold}55` }}>
          <TrendingUp size={11} /> My side of the story
        </div>
        <h3 className="font-black tracking-[-0.02em] leading-[1.02]"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3.2rem)", color: "white" }}>
          Pam, the accident attorney in Irving —{" "}
          <span style={{
            backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>{p.revenueMultiplier} the money.</span>
        </h3>

        {/* KPIs */}
        <div className="mt-10 grid grid-cols-3 gap-4">
          <KPI label="Before" value={p.before} tone="soft" />
          <KPI label="After" value={p.after} tone="pop" />
          <KPI label="Timeline" value={p.timeline} tone="soft" />
        </div>

        <div className="mt-10 flex items-start gap-4 max-w-[860px]">
          <Quote size={28} className="shrink-0" style={{ color: teal }} />
          <p className="text-lg md:text-xl leading-relaxed text-white/90 italic">&ldquo;{p.quote}&rdquo;</p>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: C.textFade }}>
          <span>— Kit,</span> <span>founder, PlugitAI</span>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, tone }) {
  const isPop = tone === "pop";
  return (
    <div className="rounded-2xl p-5 border"
         style={{
           background: isPop ? `linear-gradient(135deg, ${teal}20 0%, ${gold}15 100%)` : "rgba(17,24,39,0.55)",
           borderColor: isPop ? `${gold}55` : C.line,
           backdropFilter: "blur(10px)",
         }}>
      <div className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: isPop ? gold : C.textFade }}>{label}</div>
      <div className={`font-black leading-tight ${isPop ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`}
           style={isPop ? { color: "white", textShadow: `0 0 24px ${gold}66` } : { color: "white" }}>
        {value}
      </div>
    </div>
  );
}

function TestimonialCard({ t, idx }) {
  const accent = idx % 3 === 0 ? teal : idx % 3 === 1 ? C.blueGlow : C.greenGlow;
  return (
    <div
      className="mb-4 md:mb-5 break-inside-avoid rounded-2xl p-5 border relative overflow-hidden"
      style={{
        background: t.verified ? `linear-gradient(135deg, ${gold}12 0%, ${teal}0e 100%)` : "rgba(17,24,39,0.55)",
        borderColor: t.verified ? `${gold}66` : C.line,
        backdropFilter: "blur(10px)",
      }}
      data-testid={`testimonial-${idx}`}
    >
      {t.verified && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.18em] gold-shine">
          <ShieldCheck size={10} style={{ color: gold }} /> Verified
        </div>
      )}
      <Quote size={16} style={{ color: accent }} />
      <p className="mt-2 text-sm md:text-[15px] leading-relaxed text-white/90">&ldquo;{t.quote}&rdquo;</p>
      <div className="mt-4 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: `${C.line}` }}>
        <div className="min-w-0">
          <div className="text-sm font-bold text-white truncate">{t.name}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] truncate" style={{ color: C.textFade }}>{t.role}</div>
        </div>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] shrink-0" style={{ color: accent }}>
          <MapPin size={10} /> {t.city}
        </div>
      </div>
    </div>
  );
}
